import React, { useState } from "react";
import { Box, Typography, Paper, Chip, Button, FormControlLabel, Checkbox } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { PrintHeader, PrintHeaderSpacer, PrintFooter, PrintFooterSpacer } from "../../utils/PrintHeader";

// Hauteur de la carte de duel elle-même, hauteur de la ligne d'étiquette
// (numéro de heat) réservée au-dessus, et espace de base entre 2 duels du
// 1er tour — la hauteur totale (étiquette + carte) sert de référence pour
// calculer l'espacement des tours suivants, que l'étiquette soit affichée
// ou non (hauteur toujours réservée, pour ne jamais casser le calcul).
const BOX_HEIGHT = 64;
const LABEL_HEIGHT = 16;
const MATCH_HEIGHT = BOX_HEIGHT + LABEL_HEIGHT;
const BASE_GAP = 14;

// Les libellés de tour arrivent en français depuis les données du bracket
// (générées ailleurs, ex. Brackets.jsx) — traduits ici uniquement pour
// l'affichage de CE document, même logique que STAGE_TRANSLATIONS dans
// RankingTable.jsx.
const ROUND_TRANSLATIONS = {
    "1/32 de finale": "1/32 Finals",
    "1/16 de finale": "1/16 Finals",
    "1/8 de finale": "1/8 Finals",
    "Quarts de finale": "1/4 Finals",
    "Demi-finales": "1/2 Finals",
    "Finale": "Final",
    "Petite finale": "Small Final",
};

function translateRoundLabel(label) {
    return ROUND_TRANSLATIONS[label] || label;
}

// Pour le tour d'indice `roundIndex` (0 = premier tour), calcule l'espace
// entre 2 duels consécutifs de CE tour, et le décalage à appliquer avant
// le tout premier duel — de façon à ce que chaque duel d'un tour soit
// centré verticalement pile entre ses deux duels "parents" du tour
// précédent (centrage récursif classique d'un tableau à élimination).
function roundSpacing(roundIndex) {
    const factor = Math.pow(2, roundIndex);
    const centerToCenter = factor * (MATCH_HEIGHT + BASE_GAP);
    const marginBottom = centerToCenter - MATCH_HEIGHT;
    const marginTopFirst = marginBottom / 2;
    return { marginBottom, marginTopFirst };
}

// Case d'un duel dans le tableau des paires — lecture seule (pas de bouton
// gagnant/couleur ici, c'est juste la vue "cheminement"). `heatLabel`
// (ex: "32.1") s'affiche sur une ligne réservée au-dessus de la carte —
// vide (mais toujours présente) si non fourni, pour garder une hauteur
// totale identique sur tous les duels et ne pas fausser le centrage.
function PairBox({ match, heatLabel }) {
    const renderAthlete = (athlete, color, isWinner) => {
        if (!athlete) return <Typography variant="caption" color="text.secondary">—</Typography>;
        return (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                <Typography
                    variant="caption"
                    noWrap
                    sx={{
                        maxWidth: 145,
                        fontWeight: isWinner ? "bold" : "normal",
                        color: isWinner ? "#2e7d32" : "text.primary",
                    }}
                >
                    {isWinner && "🏆 "}[{athlete.qualifRank ?? athlete.bib}] {athlete.name}
                </Typography>
                {color && (
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: "bold",
                            color: color === "blue" ? "#1976d2" : "#d32f2f",
                            border: "1px solid",
                            borderColor: color === "blue" ? "#1976d2" : "#d32f2f",
                            borderRadius: 1,
                            px: 0.5,
                            flexShrink: 0,
                            lineHeight: 1.5,
                        }}
                    >
                        {color === "blue" ? "BLUE" : "RED"}
                    </Typography>
                )}
            </Box>
        );
    };

    const winnerFiscode = match.winner?.fiscode;
    const isWinnerA = !!winnerFiscode && match.athleteA?.fiscode === winnerFiscode;
    const isWinnerB = !!winnerFiscode && match.athleteB?.fiscode === winnerFiscode;

    return (
        <Box sx={{ width: 190 }}>
            <Typography
                variant="caption"
                sx={{
                    display: "block",
                    height: LABEL_HEIGHT,
                    lineHeight: `${LABEL_HEIGHT}px`,
                    fontWeight: "bold",
                    color: "text.secondary",
                    visibility: heatLabel ? "visible" : "hidden",
                }}
            >
                {heatLabel || "."}
            </Typography>
            <Paper
                variant="outlined"
                sx={{
                    p: 1,
                    height: BOX_HEIGHT,
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    borderLeft: match.winner ? "4px solid #2e7d32" : "4px solid transparent",
                }}
            >
                {renderAthlete(match.athleteA, match.colorA, isWinnerA)}
                <Box sx={{ borderTop: "1px solid #e0e0e0", my: 0.5 }} />
                {renderAthlete(match.athleteB, match.colorB, isWinnerB)}
            </Paper>
        </Box>
    );
}

// Tableau des paires : chaque tour est une colonne. Le duel n (tour i+1)
// est positionné exactement au milieu vertical des duels 2n et 2n+1 du
// tour i, en calculant les marges via roundSpacing() — plutôt qu'un simple
// espacement approximatif.
//
// Modifié le 01.10.2026 : impression SANS bandeau/logos par défaut (le
// tableau a besoin de toute la hauteur de la page). Une case permet de
// les remettre si besoin.
export default function PairingSheet({ bracket, branding }) {
    const [withBranding, setWithBranding] = useState(false);

    if (!bracket || !bracket.rounds) return null;

    return (
        <Paper
            sx={{
                p: 3,
                overflowX: "auto",
                "@media print": {
                    boxShadow: "none",
                    border: "none",
                    p: 0,
                    px: withBranding ? "10mm" : 0,
                },
            }}
        >
            <style>{`
                @media print {
                    /* Un bracket est naturellement plus large que haut —
                       le format portrait par défaut gaspille l'espace.
                       Avec en-tête : marge 0, le bandeau et le pied de page
                       (position fixed dans PrintHeader/PrintFooter) touchent
                       les bords de la feuille, et les espaces sont gérés par
                       PrintHeaderSpacer/PrintFooterSpacer. Sans en-tête :
                       simple marge de 10 mm tout autour. */
                    @page {
                        size: landscape;
                        margin: ${withBranding ? "0" : "10mm"};
                    }
                    /* Évite qu'une carte de duel soit coupée en deux
                       entre deux pages. */
                    .pairing-match-box {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                }
            `}</style>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    gap: 2,
                    flexWrap: "wrap",
                    "@media print": { display: "none" },
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    Pairing sheet
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <FormControlLabel
                        control={<Checkbox checked={withBranding} onChange={(e) => setWithBranding(e.target.checked)} />}
                        label="Imprimer avec bandeau / logos"
                    />
                    <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
                        Print
                    </Button>
                </Box>
            </Box>

            {withBranding && (
                <>
                    <PrintHeader branding={branding} />
                    <PrintHeaderSpacer branding={branding} />
                </>
            )}

            <Typography
                variant="h5"
                align="center"
                sx={{ mb: 3, fontWeight: "bold", display: "none", "@media print": { display: "block" } }}
            >
                Pairing sheet
            </Typography>

            <Box sx={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
                {bracket.rounds.map((round, i) => {
                    const { marginBottom, marginTopFirst } = roundSpacing(i);
                    const isFinal = i === bracket.rounds.length - 1;
                    // Préfixe du numéro de heat = nombre de duels de CE
                    // tour (32 concurrents -> 16 duels -> préfixe 16 ;
                    // 16 concurrents -> 8 duels -> préfixe 8, etc.). Pas
                    // affiché pour la finale (un seul duel).
                    const heatPrefix = round.length;

                    return (
                        <Box
                            key={i}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                minWidth: 190,
                            }}
                        >
                            <Typography variant="subtitle2" align="center" sx={{ mb: 1, fontWeight: "bold" }}>
                                {round[0]?.label ? translateRoundLabel(round[0].label) : `Round ${i + 1}`}
                            </Typography>
                            {round.map((match, mi) => (
                                <Box
                                    key={match.id}
                                    className="pairing-match-box"
                                    sx={{
                                        mt: mi === 0 ? `${marginTopFirst}px` : 0,
                                        mb: `${marginBottom}px`,
                                    }}
                                >
                                    <PairBox
                                        match={match}
                                        heatLabel={isFinal ? null : `${heatPrefix}.${mi + 1}`}
                                    />
                                </Box>
                            ))}
                        </Box>
                    );
                })}

                {bracket.smallFinal && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            minWidth: 190,
                            alignSelf: "center",
                        }}
                    >
                        <Chip label="Small Final (3rd/4th)" size="small" sx={{ mb: 1 }} />
                        <PairBox match={bracket.smallFinal} />
                    </Box>
                )}
            </Box>

            {withBranding && (
                <>
                    <PrintFooterSpacer branding={branding} />
                    <PrintFooter branding={branding} />
                </>
            )}
        </Paper>
    );
}